/**
 * Highlighter — renders a string with "marker pen" style highlights
 * on any phrase the author wrapped in `**double asterisks**`.
 *
 * Explicit-only (Feb 2026): the course-tips LLM prompt now instructs
 * Claude to wrap 1-3 load-bearing facts per tip body in `**...**`.
 * That's the single source of truth for what deserves a highlighter
 * — no client-side pattern-matching, no auto-detection.
 *
 * Visual: a soft amber marker band sits behind the text, clipped to
 * the bottom half of the line so it reads as a real highlighter
 * rather than a filled rectangle. `box-decoration-break: clone`
 * keeps the effect intact when a marked phrase wraps mid-line.
 */
import React from "react";


function explicitMarkParts(text) {
  const rx = /\*\*(.+?)\*\*/g;
  const parts = [];
  let last = 0;
  let m;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index), mark: false });
    parts.push({ text: m[1], mark: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), mark: false });
  return parts.length ? parts : [{ text, mark: false }];
}


export default function Highlighter({ children, className = "" }) {
  if (typeof children !== "string" || children.length === 0) {
    return <>{children}</>;
  }
  if (!children.includes("**")) {
    // Fast path — no marks to render, no wrapper needed.
    return <>{children}</>;
  }
  const parts = explicitMarkParts(children);
  return (
    <span className={className}>
      {parts.map((p, i) =>
        p.mark ? (
          <mark
            key={i}
            data-testid="highlighter-mark"
            className="bg-[linear-gradient(180deg,transparent_55%,rgba(253,224,71,0.75)_55%,rgba(253,224,71,0.75)_92%,transparent_92%)] text-stone-900 rounded-sm px-0.5 -mx-0.5"
            style={{
              WebkitBoxDecorationBreak: "clone",
              boxDecorationBreak: "clone",
              display: "inline",
            }}
          >
            {p.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{p.text}</React.Fragment>
        )
      )}
    </span>
  );
}
